import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { AlertTriangle, X } from "lucide-react";
import { useTranslation } from "react-i18next";

export type WizardAbandonMode = "create" | "edit";

export interface WizardAbandonCopy {
  title?: string;
  description?: string;
}

interface WizardAbandonDialogProps {
  open: boolean;
  mode: WizardAbandonMode;
  copy?: WizardAbandonCopy;
  onClose: () => void;
  onAbandon: () => void;
  onSaveDraft?: () => void;
}

const WizardAbandonDialog: React.FC<WizardAbandonDialogProps> = ({
  open,
  mode,
  copy,
  onClose,
  onAbandon,
  onSaveDraft,
}) => {
  const { t } = useTranslation("wizard-abandon");
  const theme = useTheme();

  const titleKey = mode === "edit" ? "edit.title" : "create.title";
  const descKey = mode === "edit" ? "edit.description" : "create.description";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          padding: "12px 12px 0",
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{ color: theme.palette.text.primary }}
        >
          <X size={20} />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: 1.5,
          padding: "0 24px 8px",
        }}
      >
        <AlertTriangle size={44} color={theme.palette.warning.main} />
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: 700,
            color: theme.palette.text.primary,
            lineHeight: 1.3,
          }}
        >
          {copy?.title ?? t(titleKey)}
        </Typography>
        <Typography
          sx={{
            fontSize: "14px",
            color: theme.palette.text.primary,
            lineHeight: 1.5,
          }}
        >
          {copy?.description ?? t(descKey)}
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{ padding: "16px 24px 20px", justifyContent: "center", gap: 1 }}
      >
        {mode === "edit" ? (
          <>
            <Button
              onClick={onAbandon}
              variant="outlined"
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "8px",
                whiteSpace: "nowrap",
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary,
              }}
            >
              {t("edit.abandon")}
            </Button>
            <Button
              onClick={onClose}
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "8px",
                whiteSpace: "nowrap",
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                "&:hover": { backgroundColor: theme.palette.primary.dark },
              }}
            >
              {t("edit.continue")}
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={onAbandon}
              variant="outlined"
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "8px",
                whiteSpace: "nowrap",
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary,
              }}
            >
              {t("create.abandon")}
            </Button>
            {onSaveDraft && (
              <Button
                onClick={onSaveDraft}
                variant="contained"
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "8px",
                  whiteSpace: "nowrap",
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  "&:hover": { backgroundColor: theme.palette.primary.dark },
                }}
              >
                {t("create.save-draft")}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WizardAbandonDialog;
